/**
 * Focus after a failed submit (FRM-005, A11Y-010): `focusErrors()` is called by the handler that
 * has just put the errors into state; once they have rendered, focus goes to
 * 1. the error summary of a long form (`[data-error-summary]`), else
 * 2. the first invalid field in the DOM — which is the form's visual order, never object-key
 *    order (`aria-invalid="true"`, which an MUI field sets from its `error` prop), else
 * 3. the form-level error alert ("Some details need attention.").
 *
 * It only runs on a call, so a re-render never moves focus.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** The element a failed submit should move focus to inside `root`, or null. */
export function firstErrorTarget(root: ParentNode): HTMLElement | null {
  const summary = root.querySelector<HTMLElement>('[data-error-summary]');
  if (summary !== null) return summary;
  const field = root.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (field !== null) return field;
  return root.querySelector<HTMLElement>('[role="alert"]');
}

export function focusFirstError(root: ParentNode): boolean {
  const target = firstErrorTarget(root);
  if (target === null) return false;
  // An alert or summary is not a control: it takes programmatic focus only.
  if (
    !target.hasAttribute('tabindex') &&
    !/^(INPUT|TEXTAREA|SELECT|BUTTON|A)$/.test(target.tagName)
  ) {
    target.setAttribute('tabindex', '-1');
  }
  target.focus();
  return true;
}

export interface ErrorFocus<E extends HTMLElement> {
  /** Callback ref: attach to the element wrapping the form's fields and alert. */
  rootRef: (element: E | null) => void;
  /** The attached element, for event handlers and effects (never during render). */
  getRoot: () => E | null;
  /** Call right after setting the errors; focus moves once they have rendered. */
  focusErrors: () => void;
}

/**
 * `trigger` is for errors that arrive as props (a dialog whose host owns the request): focus also
 * moves when it **changes** after mount — the value the dialog opened with is not a failure of its
 * own. With no attached root nothing is focused.
 */
export function useErrorFocus<E extends HTMLElement = HTMLDivElement>(trigger = 0): ErrorFocus<E> {
  const root = useRef<E | null>(null);
  const [requests, setRequests] = useState(0);
  const rootRef = useCallback((element: E | null) => {
    root.current = element;
  }, []);
  const getRoot = useCallback(() => root.current, []);
  const seenTrigger = useRef(trigger);
  useEffect(() => {
    if (requests === 0) return;
    if (root.current !== null) focusFirstError(root.current);
  }, [requests]);
  useEffect(() => {
    if (trigger === seenTrigger.current) return;
    seenTrigger.current = trigger;
    if (root.current !== null) focusFirstError(root.current);
  }, [trigger]);
  const focusErrors = useCallback(() => setRequests((n) => n + 1), []);
  return { rootRef, getRoot, focusErrors };
}
